/**
 * User Ask Card
 *
 * Renders a structured question from the assistant with CLICKABLE answer
 * options (askUser tool). A click sends the option label as a regular chat
 * message; multi-select collects options and sends them comma-separated.
 * Free-text via the normal input stays possible at any time.
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, Chip, Button, ButtonBase } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import SendIcon from '@mui/icons-material/Send';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useAIChat } from '../../../hooks/use-ai-chat';

export interface UserAskCardData {
  questionId: string;
  question: string;
  header?: string;
  options: Array<{ label: string; description?: string }>;
  multiSelect?: boolean;
}

const UserAskCard: React.FC<{ data: UserAskCardData }> = ({ data }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { send, isSending } = useAIChat();
  const [answered, setAnswered] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  const options = data.options || [];
  const disabled = answered !== null || isSending;

  const submitAnswer = (answer: string) => {
    if (!answer || disabled) return;
    send(answer);
    setAnswered(answer);
  };

  const toggleOption = (label: string) => {
    if (disabled) return;
    setSelected((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  return (
    <Paper className={classes.card} elevation={0}>
      <Box className={classes.header}>
        <HelpOutlineIcon className={classes.icon} fontSize="small" />
        {data.header && <Chip size="small" label={data.header} className={classes.headerChip} />}
        <Typography variant="subtitle2" className={classes.question}>
          {data.question}
        </Typography>
      </Box>

      <Box className={classes.options}>
        {options.map((option) => {
          const isPicked = answered === option.label
            || (answered !== null && data.multiSelect && answered.includes(option.label))
            || selected.includes(option.label);
          return (
            <ButtonBase
              key={option.label}
              className={cx(classes.option, isPicked && classes.optionPicked, disabled && classes.optionDisabled)}
              onClick={() => (data.multiSelect ? toggleOption(option.label) : submitAnswer(option.label))}
              disabled={disabled}
              focusRipple
            >
              {data.multiSelect && (
                isPicked
                  ? <CheckBoxIcon fontSize="small" className={classes.optionCheck} />
                  : <CheckBoxOutlineBlankIcon fontSize="small" className={classes.optionCheck} />
              )}
              <Box className={classes.optionText}>
                <Typography variant="body2" className={classes.optionLabel}>
                  {option.label}
                </Typography>
                {option.description && (
                  <Typography variant="caption" color="textSecondary">
                    {option.description}
                  </Typography>
                )}
              </Box>
              {!data.multiSelect && answered === option.label && (
                <CheckCircleIcon color="success" fontSize="small" />
              )}
            </ButtonBase>
          );
        })}
      </Box>

      {data.multiSelect && answered === null && (
        <Button
          size="small"
          variant="contained"
          startIcon={<SendIcon />}
          disabled={selected.length === 0 || disabled}
          onClick={() => submitAnswer(selected.join(', '))}
          className={classes.submit}
        >
          {t('userAskSubmit', 'Auswahl senden')}
        </Button>
      )}

      {answered !== null ? (
        <Typography variant="caption" color="textSecondary">
          {t('userAskAnswered', 'Beantwortet: {{answer}}', { answer: answered })}
        </Typography>
      ) : (
        <Typography variant="caption" color="textSecondary">
          {t('userAskFreeTextHint', 'Oder einfach unten eine eigene Antwort tippen.')}
        </Typography>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.primary.main}33`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxWidth: 520,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : `${theme.palette.primary.main}06`,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  icon: {
    color: theme.palette.primary.main,
    marginTop: 2,
  },
  headerChip: {
    flexShrink: 0,
  },
  question: {
    fontWeight: 600,
  },
  options: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'left',
    justifyContent: 'flex-start',
    width: '100%',
    transition: theme.transitions.create(['border-color', 'background-color'], { duration: 120 }),
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}0d`,
    },
  },
  optionPicked: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}14`,
  },
  optionDisabled: {
    opacity: 0.65,
  },
  optionCheck: {
    color: theme.palette.primary.main,
  },
  optionText: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  optionLabel: {
    fontWeight: 500,
  },
  submit: {
    alignSelf: 'flex-start',
  },
}));

export default UserAskCard;
